/*
 * Copyright Amazon.com, Inc. and its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: MIT
 *
 * Licensed under the MIT License. See the LICENSE accompanying this file
 * for the specific language governing permissions and limitations under
 * the License.
 */

import React, { useEffect } from "react";
import { connect } from "react-redux";

import {
	Heading,
	Authenticator,
	useAuthenticator,
	useTheme,
} from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
// import logoLight from '../../assets/Logos/logo-light.png';
import logoLight from "../../assets/Logos/wave-logo.svg";
// import useWindowDimensions from '../../components/ViewPort/useWindowDimensions';
import { Branding } from "../../branding";
import handleAuthUIStateChange from "./helpers/handleAuthStateChange";
import {
	Box,
	Typography,
	Grid,
	CardMedia,
	makeStyles,
} from "@material-ui/core";
import CircularStatic from "../CircularStatic/CircularStatic";

// import CustomSignIn from '../CustomSignIn/CustomSignIn';

const useStyles = makeStyles((theme) => ({
	container: {
		backgroundColor: "#FAFAFA",
		position: "absolute",
		top: 0,
		left: 0,
		display: "flex",
		alignItems: "baseline",
		width: "100%",
		minHeight: "100vh",
		boxSizing: "border-box",
	},
	gridLogo: {
		paddingBottom: theme.spacing(3),
	},
	grid: {
		minHeight: "100vh",
		alignItems: "center",
		justifyContent: "center",
	},
	box: {
		width: "90%",
		maxWidth: 650,
	},
	card: {
		justifyContent: "center",
	},
	cardMedia: {
		width: 300,
		height: 200,
		backgroundSize: "contain",
	},
	cardHeader: {
		paddingBottom: 0,
	},
	cardContent: {
		justifyContent: "center",
		textAlign: "center",
		"&:last-child": {
			paddingBottom: theme.spacing(1),
		},
	},
	boxExpandIdp: {
		textAlign: "center",
	},
	chipExpand: {
		backgroundColor: Branding.secondary,
		color: Branding.white,
		"&:hover": {
			backgroundColor: Branding.secondary,
			opacity: Branding.opacityHover,
		},
	},
	expandIcon: {
		color: Branding.white,
	},
	divider: {
		marginBottom: theme.spacing(2),
	},
	typoToS: {
		paddingTop: theme.spacing(3),
	},
	linkTos: {
		fontSize: 14,
		"&:hover": {
			color: Branding.accent,
			opacity: Branding.opacityHover,
		},
	},
	midBul: {
		paddingLeft: theme.spacing(1),
		paddingRight: theme.spacing(1),
		color: Branding.secondary,
	},
	linkImprint: {
		fontSize: 14,
		"&:hover": {
			cursor: "pointer",
			color: Branding.accent,
			opacity: Branding.opacityHover,
		},
	},
}));

const mapStateToProps = (state) => {
	return {
		lang: state.app.lang,
	};
};

const SignInContainer = () => {
	const classes = useStyles();

	return (
		<div className={classes.container}>
			<Grid container className={classes.grid}>
				<Authenticator components={components}>
					{({ signOut, user }) => (
						<SignedIn user={user} signOut={signOut} />
					)}
				</Authenticator>
			</Grid>
		</div>
	);
};

const SignedIn = ({ user, signOut }) => {
	const { route } = useAuthenticator();

	React.useEffect(() => {
		console.log(route);
		handleAuthUIStateChange(route);
	}, [route]);

	return (
		<Box sx={{ display: "flex" }}>
			<CircularStatic />
		</Box>
	);
};

const components = {
	Header() {
		const classes = useStyles();

		return (
			<Grid
				container
				spacing={0}
				direction="column"
				alignItems="center"
				justify="center"
			>
				<CardMedia
					className={classes.cardMedia}
					image={logoLight}
					title={Branding.appName}
				/>
			</Grid>
		);
	},
	SignIn: {
		Header() {
			const { tokens } = useTheme();
			useEffect(() => {
				(() => {
					function checkUrlForCN() {
						const cnUrlPart = "wavebl-cn.com";
						const url = window.location.href;
						const urlParams = new URLSearchParams(
							window.location.search
						);

						if (url.includes(cnUrlPart)) {
							return true;
						}

						for (let param of urlParams.entries()) {
							if (param[1].includes(cnUrlPart)) {
								return true;
							}
						}

						return false;
					}

					function icpTag() {
						const anchor = document.createElement("a");
						anchor.href =
							"https://beian.miit.gov.cn/#/Integrated/recordQuery";
						anchor.target = "_blank";
						anchor.textContent = "苏ICP备2024066455号-3";
						anchor.style =
							"margin-top:8px;display:block;text-align:center;";
						return anchor;
					}

					if (checkUrlForCN()) {
						const formContainer = document.querySelector(
							"div[data-amplify-container]"
						);
						formContainer?.appendChild(icpTag());
					}
				})();
			}, []);
			return (
				<Grid
					container
					spacing={0}
					direction="column"
					alignItems="center"
					justify="center"
				>
					<Heading padding={`${tokens.space.xl} 0 0 0`} level={5}>
						Log in to your account
					</Heading>
				</Grid>
			);
		},
	},
	ResetPassword: {
		Header() {
			const { tokens } = useTheme();

			return (
				<Grid
					container
					spacing={0}
					direction="column"
					alignItems="center"
					justify="center"
				>
					<Heading padding={`0 0 ${tokens.space.xl} 0`} level={5}>
						Reset your password
					</Heading>
				</Grid>
			);
		},
	},
	ConfirmResetPassword: {
		Header() {
			const { tokens } = useTheme();

			return (
				<Grid
					container
					spacing={0}
					direction="column"
					alignItems="center"
					justify="center"
				>
					<Heading padding={`0 0 ${tokens.space.xl} 0`} level={5}>
						Reset your password
					</Heading>
					<Box>
						<Typography>
							A verification code has been sent to your email
						</Typography>
					</Box>
				</Grid>
			);
		},
	},
	ForceNewPassword: {
		FormFields() {
			return (
				<>
					<Authenticator.ForceNewPassword.FormFields />
					<Box>
						<Typography>
							The password must contain at least 8 characters and
							include at least: lowercase, uppercase, a number,
							and one of the following characters ! @ # $ & *
						</Typography>
					</Box>
				</>
			);
		},
	},
};

export default connect(mapStateToProps)(SignInContainer);
